// 서비스 워커 등록 · 업데이트 안내 — 프레임워크 없이(바닐라 앱도 쓴다). React 훅은 react.js.
//
// **보안 연결(https · localhost)에서만 건다.** http 로 들어온 설치에서는 브라우저가 SW 를 아예 안 받아
// 등록을 시도하면 콘솔에 에러만 남는다 — 그래서 조용히 건너뛰고 화면은 SW 없이 돈다. https 를 안 켠 것은
// 운영자의 선택이지 고장이 아니다. https 에서 등록이 **실패**한 것은 고장이라 console.warn 으로 남긴다.
//
// **자동으로 reload 하지 않는다.** 최초 설치에서도 activate 의 `self.clients.claim()` 이 controllerchange 를
// 울린다(컨트롤러가 null → 워커로 바뀌는 것도 같은 이벤트다). 그 순간 새로고침하면 사람이 아무것도 안
// 눌렀는데 입력 중 내용이 날아간다. 그래서 **사람이 눌렀다는 사실**(refreshing)을 따로 들고 그때만 reload 한다.
// (b3x-in src/lib/serviceWorker.ts · swUpdate.ts 를 올린 것이다.)

/** 열어 둔 탭이 새 빌드를 알아채는 간격. 내비게이션 없이 열어 둔 탭은 브라우저가 스스로 다시 확인하지 않는다. */
export const UPDATE_CHECK_MS = 5 * 60_000;

/**
 * 창 하나에 묶인 등록기. 시험은 가짜 창을 넘기고, 화면은 아래의 기본 판(globalThis)을 쓴다.
 * @param {any} win
 */
export function createPwa(win = globalThis) {
  /** @type {any} */
  let registration = null;
  let refreshing = false;
  let reloading = false;
  const listeners = new Set();
  const notify = () => listeners.forEach((l) => l());

  /** 새 판이 기다리고 있나. */
  function hasWaiting() {
    return !!(registration && registration.waiting);
  }

  /** @param {() => void} listener @returns {() => void} */
  function subscribeWaiting(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }

  /** 등록에 성공한 뒤 부른다. */
  function watchForUpdate(reg, { intervalMs = UPDATE_CHECK_MS } = {}) {
    registration = reg;
    if (reg.waiting) notify();
    reg.addEventListener("updatefound", () => {
      const installing = reg.installing;
      if (!installing) return;
      // 최초 설치도 installed 를 지난다 — 그 짧은 순간 reg.waiting 이 그 워커라, 그때 알리면 「새 판이 있습니다」가
      // 첫 방문부터 떠서 안 꺼진다(store-mops 실측). 기존 컨트롤러가 있을 때만 「새 판」이다. activated · redundant
      // 에서도 다시 알려 기다리는 판이 사라진 것(다른 탭이 적용함 등)을 안내가 따라가게 한다.
      const controlled = !!win.navigator.serviceWorker.controller;
      installing.addEventListener("statechange", () => {
        if (installing.state === "installed" && controlled) notify();
        if (installing.state === "activated" || installing.state === "redundant") notify();
      });
    });
    win.navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (!refreshing || reloading) return;
      reloading = true;
      win.location.reload();
    });
    if (intervalMs > 0) {
      win.setInterval(() => {
        // 조용히 넘어간다 — 못 닿은 사실은 연결 안내(connectivity.js)가 이미 말한다.
        Promise.resolve(reg.update()).catch(() => {});
      }, intervalMs);
    }
  }

  /** 안내의 「새로고침」 단추. 자동으로는 절대 안 부른다 — 입력 중 내용이 날아간다. */
  function applyUpdate() {
    const waiting = registration && registration.waiting;
    if (!waiting) return;
    refreshing = true;
    waiting.postMessage({ type: "SKIP_WAITING" });
  }

  /**
   * @param {{ url?: string, enabled?: boolean, intervalMs?: number }} [opts]
   *   enabled — 배포 빌드일 때만 참으로(`import.meta.env.PROD`). 개발 서버에는 sw.js 가 없고, 있었다면 HMR 모듈을
   *   캐시해 화면이 안 바뀐다. 공통 모듈은 빌드 도구를 모르므로 서비스가 넘긴다.
   * @returns {"skipped-dev" | "skipped-insecure" | "scheduled"} 시험·진단용
   */
  function registerServiceWorker({ url = "/sw.js", enabled = true, intervalMs } = {}) {
    if (!enabled) return "skipped-dev";
    if (!win.isSecureContext || !win.navigator || !("serviceWorker" in win.navigator)) return "skipped-insecure";
    // load 뒤에 건다 — 첫 화면이 뜨는 동안 SW 설치(앱 셸 받기)가 대역폭을 다투지 않게.
    win.addEventListener("load", () => {
      Promise.resolve(win.navigator.serviceWorker.register(url))
        .then((reg) => watchForUpdate(reg, { intervalMs }))
        .catch((err) => {
          // 실패해도 화면은 캐시 없이 그대로 돈다 — 그 사실과 볼 곳을 남긴다(화면이 아니라 개발자 콘솔이다).
          win.console.warn(
            `서비스 워커(${url}) 등록 실패 — 화면은 캐시 없이 그대로 돕니다. ` +
              `${url} 가 404 이거나 text/html 이면 웹 이미지를 다시 지으세요(빌드에 mopsServiceWorker 플러그인이 있나), ` +
              "인증서 오류면 기기에 사설 CA 를 등록하세요.",
            err,
          );
        });
    });
    return "scheduled";
  }

  return { registerServiceWorker, watchForUpdate, hasWaiting, subscribeWaiting, applyUpdate };
}

let shared = null;
function pwa() {
  shared = shared || createPwa(globalThis);
  return shared;
}

/** @param {Parameters<ReturnType<typeof createPwa>["registerServiceWorker"]>[0]} [opts] */
export const registerServiceWorker = (opts) => pwa().registerServiceWorker(opts);
export const hasWaiting = () => pwa().hasWaiting();
/** @param {() => void} listener */
export const subscribeWaiting = (listener) => pwa().subscribeWaiting(listener);
export const applyUpdate = () => pwa().applyUpdate();
